import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2, Megaphone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { EVENTS, track } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BUDGET_OPTIONS = [
  { value: "", label: "Select a range (optional)" },
  { value: "under-1k", label: "Under $1,000 / month" },
  { value: "1k-5k", label: "$1,000 – $5,000 / month" },
  { value: "5k-15k", label: "$5,000 – $15,000 / month" },
  { value: "15k-plus", label: "$15,000+ / month" },
  { value: "one-time", label: "One-time campaign" },
];

// Pre-filled sponsorship inquiry template. Kept short and editable — the
// partner can replace or edit before submitting.
const MESSAGE_TEMPLATE = `Hi Parlay Assistant team,

I'm interested in exploring a sponsorship on your site. Here's a bit about us:

- Company / community:
- Product or offer:
- Campaign goals:
- Preferred placement (sponsor slot, results banner, etc.):
- Timeline:

Looking forward to hearing back.`;

const emptyForm = {
  name: "",
  email: "",
  company: "",
  website: "",
  budget: "",
  message: MESSAGE_TEMPLATE,
};

// Very lightweight client-side email shape check — the backend enforces
// the real validation via pydantic EmailStr.
const isEmailShape = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export const AdvertiseDialog = ({ triggerClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter an email address.";
    else if (!isEmailShape(form.email.trim())) next.email = "That email doesn't look right.";
    if (!form.message.trim() || form.message.trim().length < 10)
      next.message = "Tell us a little about your campaign (10+ characters).";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const resetAll = () => {
    setForm(emptyForm);
    setErrors({});
    setSubmitted(false);
    setSubmitting(false);
  };

  const handleOpenChange = (next) => {
    if (next && !open) {
      // Fire the analytics event only when we transition to open, not on close.
      track(EVENTS.ADVERTISE_WITH_US_CLICKED, {
        element_clicked: "advertise-with-us-btn",
      });
    }
    setOpen(next);
    if (!next) {
      // Give the close animation a beat, then wipe the form for next time.
      setTimeout(resetAll, 200);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/advertise`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          website: form.website.trim() || null,
          budget: form.budget || null,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) {
        let msg = "Something went wrong. Please try again in a moment.";
        try {
          const body = await res.json();
          if (body && typeof body.detail === "string") msg = body.detail;
        } catch {
          /* ignore */
        }
        toast.error(msg);
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="advertise-with-us-btn"
          className={`pa-btn pa-btn-primary ${triggerClassName}`}
        >
          Advertise With Us
          <ArrowUpRight size={16} aria-hidden="true" />
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="advertise-dialog"
        className="max-w-lg pa-dialog"
      >
        {submitted ? (
          <div
            data-testid="advertise-success"
            className="flex flex-col items-center text-center py-6"
          >
            <div
              aria-hidden="true"
              className="w-14 h-14 rounded-full inline-flex items-center justify-center mb-4"
              style={{
                background: "rgba(16,185,129,0.15)",
                border: "1px solid rgba(16,185,129,0.5)",
                color: "#10b981",
              }}
            >
              <CheckCircle2 size={26} />
            </div>
            <h3 className="font-display text-2xl uppercase tracking-tight text-slate-900">
              Message Received
            </h3>
            <p className="mt-2 text-slate-600 text-sm md:text-base">
              Thanks for reaching out — our partnerships team will review your inquiry and get
              back to you at <span className="font-medium">{form.email}</span>. Expect a reply
              within one business day.
            </p>
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              data-testid="advertise-close"
              className="pa-btn pa-btn-primary mt-6"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div
                  aria-hidden="true"
                  className="w-9 h-9 rounded-lg inline-flex items-center justify-center"
                  style={{
                    background: "rgba(16,185,129,0.14)",
                    color: "#059669",
                    border: "1px solid rgba(16,185,129,0.3)",
                  }}
                >
                  <Megaphone size={18} />
                </div>
                <DialogTitle className="font-display text-2xl uppercase tracking-tight text-slate-900">
                  Advertise With Us
                </DialogTitle>
              </div>
              <DialogDescription className="text-slate-600 mt-2">
                Tell us about your brand or campaign. We&rsquo;ll follow up within one business
                day.
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={handleSubmit}
              noValidate
              className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div className="md:col-span-1">
                <label htmlFor="ad-name" className="pa-field-label">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="ad-name"
                  data-testid="advertise-input-name"
                  className="pa-input mt-1.5"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  autoComplete="name"
                  data-invalid={errors.name ? "true" : "false"}
                  aria-invalid={!!errors.name}
                  required
                />
                {errors.name && <p className="pa-inline-error">{errors.name}</p>}
              </div>

              <div className="md:col-span-1">
                <label htmlFor="ad-email" className="pa-field-label">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="ad-email"
                  type="email"
                  data-testid="advertise-input-email"
                  className="pa-input mt-1.5"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  autoComplete="email"
                  data-invalid={errors.email ? "true" : "false"}
                  aria-invalid={!!errors.email}
                  required
                />
                {errors.email && <p className="pa-inline-error">{errors.email}</p>}
              </div>

              <div className="md:col-span-1">
                <label htmlFor="ad-company" className="pa-field-label">
                  Company
                </label>
                <input
                  id="ad-company"
                  data-testid="advertise-input-company"
                  className="pa-input mt-1.5"
                  value={form.company}
                  onChange={(e) => setField("company", e.target.value)}
                  autoComplete="organization"
                />
              </div>

              <div className="md:col-span-1">
                <label htmlFor="ad-website" className="pa-field-label">
                  Website
                </label>
                <input
                  id="ad-website"
                  data-testid="advertise-input-website"
                  className="pa-input mt-1.5"
                  value={form.website}
                  onChange={(e) => setField("website", e.target.value)}
                  placeholder="https://"
                  autoComplete="url"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="ad-budget" className="pa-field-label">
                  Monthly Budget
                </label>
                <select
                  id="ad-budget"
                  data-testid="advertise-input-budget"
                  className="pa-input mt-1.5"
                  value={form.budget}
                  onChange={(e) => setField("budget", e.target.value)}
                >
                  {BUDGET_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="ad-message" className="pa-field-label">
                  What are you looking for? <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="ad-message"
                  data-testid="advertise-input-message"
                  className="pa-input mt-1.5 min-h-[120px] resize-y"
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  rows={5}
                  placeholder="Tell us about your campaign, goals, and timeline."
                  data-invalid={errors.message ? "true" : "false"}
                  aria-invalid={!!errors.message}
                  required
                />
                {errors.message && (
                  <p className="pa-inline-error">{errors.message}</p>
                )}
              </div>

              <DialogFooter className="md:col-span-2 mt-2 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <button
                  type="button"
                  onClick={() => handleOpenChange(false)}
                  data-testid="advertise-cancel"
                  className="pa-btn pa-btn-ghost"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="advertise-submit"
                  className="pa-btn pa-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Inquiry
                      <ArrowUpRight size={16} aria-hidden="true" />
                    </>
                  )}
                </button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdvertiseDialog;
